import { collection, query, orderBy, limit, onSnapshot, where, updateDoc, doc, writeBatch } from 'firebase/firestore';
import { db } from './firebase';

export interface NotificationData {
  id: string;
  type: 'new_order' | 'low_stock' | 'user_registration' | 'user_deleted' | 'affiliate_signup' | 'order_cancelled' | 'payment_failed' | 'order_processed' | 'order_shipped' | 'order_delivered' | 'order_refunded' | 'product_review' | 'brevo_chat_message';
  title: string;
  message: string;
  data?: any;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  read: boolean;
  createdAt: any;
  emailSent?: boolean;
  emailSentAt?: any;
  readAt?: any;
}

export class NotificationService {
  /**
   * Get real-time notifications listener
   */
  static getNotificationsListener(
    callback: (notifications: NotificationData[]) => void,
    limitCount: number = 50
  ): () => void {
    const q = query(
      collection(db, 'notifications'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    return onSnapshot(q, (snapshot) => {
      const notifications: NotificationData[] = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        notifications.push({
          id: doc.id,
          type: data.type,
          title: data.title,
          message: data.message,
          data: data.data,
          priority: data.priority,
          read: data.read || false,
          createdAt: data.createdAt,
          emailSent: data.emailSent,
          emailSentAt: data.emailSentAt,
          readAt: data.readAt
        });
      });

      callback(notifications);
    }, (error) => {
      console.error('Error listening to notifications:', error);
      // Return empty array on error
      callback([]);
    });
  }

  /**
   * Get unread notifications count listener
   */
  static getUnreadCountListener(callback: (count: number) => void): () => void {
    const q = query(
      collection(db, 'notifications'),
      where('read', '==', false)
    );

    return onSnapshot(q, (snapshot) => {
      callback(snapshot.size);
    }, (error) => {
      console.error('Error listening to unread count:', error);
      callback(0);
    });
  }

  /**
   * Mark notification as read
   */
  static async markAsRead(notificationId: string): Promise<void> {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, {
        read: true,
        readAt: new Date()
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  /**
   * Mark all notifications as read
   */
  static async markAllAsRead(): Promise<void> {
    try {
      // Call the backend function to handle bulk updates
      const response = await fetch('https://us-central1-jampack-d.cloudfunctions.net/markNotificationAsRead', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ markAll: true }),
      });

      if (!response.ok) {
        throw new Error('Failed to mark all notifications as read');
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  /**
   * Get notification icon and color based on type
   */
  static getNotificationIcon(type: string) {
    switch (type) {
      case 'new_order':
      case 'order_shipped':
      case 'order_delivered':
        return { icon: 'ShoppingCart', color: 'text-jamaica-green bg-jamaica-green/10' };
      case 'order_processed':
        return { icon: 'Package', color: 'text-blue-600 bg-blue-100' };
      case 'low_stock':
        return { icon: 'AlertTriangle', color: 'text-yellow-600 bg-yellow-100' };
      case 'payment_failed':
        return { icon: 'CreditCard', color: 'text-red-600 bg-red-100' };
      case 'order_cancelled':
        return { icon: 'XCircle', color: 'text-red-600 bg-red-100' };
      case 'order_refunded':
        return { icon: 'DollarSign', color: 'text-green-600 bg-green-100' };
      case 'user_registration':
        return { icon: 'Users', color: 'text-purple-600 bg-purple-100' };
      case 'user_deleted':
        return { icon: 'UserX', color: 'text-red-700 bg-red-100' };
      case 'affiliate_signup':
        return { icon: 'Users', color: 'text-orange-600 bg-orange-100' };
      case 'product_review':
        return { icon: 'Star', color: 'text-blue-600 bg-blue-100' };
      case 'brevo_chat_message':
        return { icon: 'MessageCircle', color: 'text-blue-600 bg-blue-100' };
      default:
        return { icon: 'Bell', color: 'text-gray-600 bg-gray-100' };
    }
  }

  /**
   * Get notification priority color
   */
  static getPriorityColor(priority: string) {
    switch (priority) {
      case 'urgent':
        return 'text-red-600';
      case 'high':
        return 'text-orange-600';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
      default:
        return 'text-gray-600';
    }
  }

  /**
   * Format notification time
   */
  static formatTime(timestamp: any): string {
    if (!timestamp) return '';
    
    try {
      // Handle Firestore timestamp
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const now = new Date();
      const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

      if (diffInSeconds < 60) {
        return 'Just now';
      } else if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
      } else if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600);
        return `${hours} hour${hours > 1 ? 's' : ''} ago`;
      } else if (diffInSeconds < 604800) {
        const days = Math.floor(diffInSeconds / 86400);
        return `${days} day${days > 1 ? 's' : ''} ago`;
      } else {
        return date.toLocaleDateString();
      }
    } catch (error) {
      console.error('Error formatting notification time:', error);
      return '';
    }
  }

  /**
   * Get action URL based on notification type
   */
  static getActionUrl(notification: NotificationData): string {
    switch (notification.type) {
      case 'new_order':
      case 'order_processed':
      case 'order_shipped':
      case 'order_delivered':
      case 'order_cancelled':
      case 'order_refunded':
      case 'payment_failed':
        // Include order ID in search parameter if available
        if (notification.data?.orderId) {
          return `/orders?search=${notification.data.orderId}`;
        }
        return '/orders';
      case 'low_stock':
        // Include product name in search parameter if available
        if (notification.data?.productName) {
          return `/products?search=${encodeURIComponent(notification.data.productName)}`;
        }
        return '/inventory';
      case 'user_registration':
      case 'user_deleted':
        // Include customer email in search parameter if available
        if (notification.data?.customerEmail) {
          return `/customers?search=${encodeURIComponent(notification.data.customerEmail)}`;
        }
        return '/customers';
      case 'affiliate_signup':
        // Include applicant email in search parameter if available
        if (notification.data?.applicantEmail) {
          return `/affiliates?search=${encodeURIComponent(notification.data.applicantEmail)}`;
        }
        return '/affiliates';
      case 'product_review':
        // Include product name in search parameter if available
        if (notification.data?.productName) {
          return `/products?search=${encodeURIComponent(notification.data.productName)}`;
        }
        return '/products';
      default:
        return '/';
    }
  }
} 
